import $ from "jquery";

declare const gon: any;

interface PostBookingEventData {
    product_code?: string;
    product_name?: string;
    quantity?: number;
    duration_ms?: number;
    error_type?: string;
    message?: string;
}

/** Offers on the booking page. The value is the product code. */
const ANCILLARY_SELECTOR: string = "[data-ancillary]";
const EVENTS_URL: string = "/post_booking_events";
const CLICK_EVENTS: string[] = ["click", "touchstart"];

/** Where each flow renders the options it loaded over ajax. */
const OPTIONS_CONTAINERS: { [productCode: string]: string } = {
    BAGGAGE: "#baggage-content",
    SEATING: "#seating-content",
    UPGRADES: "#upgrades-content"
};

/**
 * A free seat on the map. Seats are picked by clicking the cell, so the grid is
 * what the customer chooses from - the checkboxes behind it are always hidden.
 */
// Any selectable seat, in either seat map. The rebuilt post-booking map
// (booking/partials/_booking_form__seating.html.erb) draws seats as
// <div class="seat ...">, and marks an occupied one by simply not giving it
// the seat class at all; the legacy table below it in the same file still
// uses <td class="seat"> with .occupiedSeat. Dropping the td qualifier
// counts both, and matches the class token "seat" only -- "seatprice" is a
// different token and is not matched.
const SEAT_CELL: string = ".seat:not(.occupiedSeat)";

const clickedAt: { [productCode: string]: number } = {};
const reportedOptions: { [productCode: string]: boolean } = {};

/** Lets the injected markup lay out before its options are counted. */
const OPTIONS_SETTLE_MS: number = 50;

/**
 * How long the customer may look at an empty panel before we record that the
 * options never turned up. Past the 99th percentile of the loads that do
 * succeed, so it reads as "this one is not coming" rather than "this was slow".
 * The timer only runs while the page is open, so the event also proves the
 * customer was still waiting rather than gone.
 */
const OPTIONS_TIMEOUT_MS: number = 30000;

const optionsTimers: { [productCode: string]: number } = {};
const reportedError: { [productCode: string]: boolean } = {};

/** The address each pending options request was sent to, keyed by product. */
const pendingUrls: { [productCode: string]: string } = {};

const reportedAbandon: { [productCode: string]: boolean } = {};
const reportedTimeout: { [productCode: string]: boolean } = {};
let watchingRequests: boolean = false;

/**
 * Builds what an event carries from the browser. Returns nothing unless
 * gon.post_booking is set. The session and all money are added server side.
 */
function eventPayload(event: string, data: PostBookingEventData): any {
    const booking = gon.post_booking;

    if (!booking) {
        return null;
    }

    return {
        order_id: booking.order_id,
        midoffice_id: booking.midoffice_id,
        search_id: booking.search_id,
        device: booking.device,
        event: event,
        url: document.location.href,
        product_code: data.product_code,
        product_name: data.product_name,
        quantity: data.quantity,
        duration_ms: data.duration_ms,
        error_type: data.error_type,
        message: data.message
    };
}

/** Sends a post-booking event, correlated by order_id. */
export function logPostBookingEvent(event: string, data: PostBookingEventData = {}): void {
    const payload = eventPayload(event, data);

    if (payload) {
        $.post(EVENTS_URL, payload);
    }
}

/**
 * Sends an event while the page is going away. A normal request dies with the
 * page, so this hands the data to the browser instead, which delivers it once
 * the page is gone. Fire and forget - there is nothing left to read a reply.
 */
function beaconPostBookingEvent(event: string, data: PostBookingEventData): void {
    if (!navigator.sendBeacon || typeof URLSearchParams === "undefined") {
        return;
    }

    const payload = eventPayload(event, data);

    if (!payload) {
        return;
    }

    const body = new URLSearchParams();

    Object.keys(payload).forEach(function (field: string): void {
        if (payload[field] !== undefined && payload[field] !== null) {
            body.append(field, String(payload[field]));
        }
    });

    try {
        navigator.sendBeacon(EVENTS_URL, body);
    } catch (_e) {
        // Leaving the page must not fail because of tracking.
    }
}

function offerOf(target: Element): HTMLElement {
    if (!target || !target.closest) {
        return null;
    }

    return target.closest(ANCILLARY_SELECTOR) as HTMLElement;
}

function productCodeOf(target: Element): string {
    const offer = offerOf(target);
    return offer ? offer.getAttribute("data-ancillary") : null;
}

function trackClick(event: Event): void {
    const offer = offerOf(event.target as Element);
    const productCode = offer ? offer.getAttribute("data-ancillary") : null;

    // One event per product per page load.
    if (!productCode || clickedAt[productCode]) {
        return;
    }

    clickedAt[productCode] = Date.now();
    logPostBookingEvent("ancillary_clicked", { product_code: productCode });

    if (OPTIONS_CONTAINERS[productCode]) {
        // data-url as well as href: the rebuilt page opens baggage and
        // seating from Stimulus <button>s (booking-support-modal#loadAncillary
        // reads the address off data-url), so there is no href to remember and
        // a failed options request could not be attributed to a product.
        rememberOptionsUrl(productCode, offer.getAttribute("href") || offer.getAttribute("data-url"));
        startOptionsTimeout(productCode);
    }
}

/**
 * Remembers where the options were asked for, so a failing request can be
 * traced back to its product - the path is localised per brand and cannot be
 * guessed. Anything too short to identify one request is dropped, since it
 * would match unrelated ones.
 */
function rememberOptionsUrl(productCode: string, href: string): void {
    if (href && href.length > 1 && href.charAt(0) !== "#") {
        pendingUrls[productCode] = href;
    }
}

/** The product whose options request went to this address, if any. */
function pendingProductFor(url: string): string {
    const codes = Object.keys(pendingUrls);

    for (let i: number = 0; i < codes.length; i++) {
        const href = pendingUrls[codes[i]];

        if (href && url.indexOf(href) !== -1) {
            return codes[i];
        }
    }

    return null;
}

/**
 * Watches every request for the ones carrying ancillary options. rails-ujs
 * announces its failures through jQuery, and mobile fires those on a different
 * copy of jQuery than this pack can reach, so the browser API is the only
 * place both devices meet.
 *
 * This wraps a global, so it never throws and always calls through. Aborts are
 * ignored on purpose - navigating away cancels pending requests and that is
 * not a failure.
 */
function watchOptionsRequests(): void {
    if (watchingRequests || typeof XMLHttpRequest === "undefined") {
        return;
    }

    watchingRequests = true;
    const open = XMLHttpRequest.prototype.open;

    XMLHttpRequest.prototype.open = function (): any {
        const xhr: XMLHttpRequest = this;
        const url: string = arguments.length > 1 ? String(arguments[1]) : "";

        try {
            xhr.addEventListener("load", function (): void {
                if (xhr.status >= 400) {
                    reportRequestFailure(url, "HTTP " + xhr.status);
                }
            });

            xhr.addEventListener("error", function (): void {
                reportRequestFailure(url, "network error");
            });
        } catch (_e) {
            // A listener we cannot attach is no reason to break the request.
        }

        return open.apply(xhr, arguments as any);
    } as any;
}

function reportRequestFailure(url: string, reason: string): void {
    try {
        const productCode = url ? pendingProductFor(url) : null;

        if (productCode) {
            reportOptionsFailed(productCode, "options_load_failed",
                                "Loading the ancillary options failed: " + reason);
        }
    } catch (_e) {
        // Reporting a failure must not become one.
    }
}

function startOptionsTimeout(productCode: string): void {
    optionsTimers[productCode] = window.setTimeout(function (): void {
        delete optionsTimers[productCode];

        const waited: number = Date.now() - clickedAt[productCode];

        // Timers pause in the back/forward cache and throttle in a background
        // tab, so a much longer wait means the page was away rather than the
        // customer watching an empty panel.
        if (waited > OPTIONS_TIMEOUT_MS * 2) {
            return;
        }

        reportedTimeout[productCode] = true;

        // Not an error - the options may still arrive. A timeout with no
        // ancillary_options_shown after it is the one that never came.
        logPostBookingEvent("ancillary_options_timeout", {
            product_code: productCode,
            duration_ms: waited
        });
    }, OPTIONS_TIMEOUT_MS);
}

function clearOptionsTimeout(productCode: string): void {
    if (optionsTimers[productCode]) {
        window.clearTimeout(optionsTimers[productCode]);
        delete optionsTimers[productCode];
    }
}

/** One error per product, and never after the options already turned up. */
function reportOptionsFailed(productCode: string, errorType: string, message: string): void {
    if (reportedOptions[productCode] || reportedError[productCode]) {
        return;
    }

    reportedError[productCode] = true;
    clearOptionsTimeout(productCode);

    logPostBookingEvent("technical_error", {
        product_code: productCode,
        error_type: errorType,
        message: message,
        duration_ms: clickedAt[productCode] ? Date.now() - clickedAt[productCode] : 0
    });
}

/**
 * Reports the products the customer asked for and never got, at the moment the
 * page goes away. Answers the one question the other events cannot: whether
 * they left or were still waiting - and after how many seconds they gave up.
 */
function reportAbandonedOptions(): void {
    Object.keys(clickedAt).forEach(function (productCode: string): void {
        if (!OPTIONS_CONTAINERS[productCode]) {
            return;
        }

        // One signal per click. A timeout already says the customer was there
        // and saw nothing, which is the part worth counting.
        if (reportedOptions[productCode] || reportedError[productCode]) {
            return;
        }

        if (reportedTimeout[productCode] || reportedAbandon[productCode]) {
            return;
        }

        reportedAbandon[productCode] = true;
        clearOptionsTimeout(productCode);

        beaconPostBookingEvent("ancillary_options_abandoned", {
            product_code: productCode,
            duration_ms: Date.now() - clickedAt[productCode]
        });
    });
}

/**
 * Only a page that is actually being destroyed counts as leaving. Switching
 * tabs or apps does not: the request keeps running, the customer may come
 * back, and reporting it would both mislabel them and throw away the timer
 * that would have caught them waiting.
 */
function watchPageLeaving(): void {
    window.addEventListener("pagehide", function (event: PageTransitionEvent): void {
        // A persisted page is frozen for the back button, not thrown away.
        if (!event.persisted) {
            reportAbandonedOptions();
        }
    });
}

function countOptions(productCode: string): number {
    const element = document.querySelector(OPTIONS_CONTAINERS[productCode]);
    if (!element) {
        return 0;
    }

    // Every leg counts, not just the open tab - the other legs are one click
    // away and their seats are on offer too.
    const seats = element.querySelectorAll(SEAT_CELL);
    if (seats.length) {
        return seats.length;
    }

    const inputs = element.querySelectorAll('input[type="checkbox"], input[type="radio"]');
    // Bags and upgrades are plain checkboxes, and a hidden one is a control the
    // customer was never offered.
    let visible: number = 0;
    for (let i: number = 0; i < inputs.length; i++) {
        if ((inputs[i] as HTMLElement).offsetParent !== null) {
            visible++;
        }
    }

    return visible;
}

function reportOptionsShown(productCode: string, durationMs: number): void {
    logPostBookingEvent("ancillary_options_shown", {
        product_code: productCode,
        quantity: countOptions(productCode),
        duration_ms: durationMs
    });
}

/** Whether a batch of mutations put any element into the panel. */
function addsElements(records: MutationRecord[]): boolean {
    for (let i: number = 0; i < records.length; i++) {
        const added = records[i].addedNodes;

        for (let j: number = 0; j < added.length; j++) {
            if (added[j].nodeType === 1) {
                return true;
            }
        }
    }

    return false;
}

/**
 * Watches the panel each flow renders its options into. The markup arrives over
 * ajax, and watching the container catches it whichever jQuery rails-ujs fires
 * on - mobile has two copies and binds to the one this pack cannot reach.
 */
function observeOptionsContainers(): void {
    if (typeof MutationObserver === "undefined") {
        return;
    }

    Object.keys(OPTIONS_CONTAINERS).forEach(function (productCode: string): void {
        const container = document.querySelector(OPTIONS_CONTAINERS[productCode]);

        if (!container) {
            return;
        }

        const observer = new MutationObserver(function (records: MutationRecord[]): void {
            // Markup lands in the panel for other reasons too, so this only
            // counts once the customer has asked for the options.
            if (!clickedAt[productCode] || reportedOptions[productCode]) {
                return;
            }

            // Replacing the panel clears it first, and an empty panel is not
            // the options arriving.
            if (!addsElements(records)) {
                return;
            }

            reportedOptions[productCode] = true;
            clearOptionsTimeout(productCode);
            delete pendingUrls[productCode];
            observer.disconnect();

            const durationMs: number = Date.now() - clickedAt[productCode];
            window.setTimeout(function (): void {
                reportOptionsShown(productCode, durationMs);
            }, OPTIONS_SETTLE_MS);
        });

        // Attributes are left out on purpose: showing the panel or hiding the
        // link is not the options arriving.
        observer.observe(container, { childList: true, subtree: true });
    });
}

function trackOptionsError(event: Event): void {
    const productCode = productCodeOf(event.target as Element);
    if (!productCode || !OPTIONS_CONTAINERS[productCode]) {
        return;
    }

    reportOptionsFailed(productCode, "options_load_failed",
                        "Loading the ancillary options failed");
}

/**
 * Reports which offer the customer pressed. Native listeners, so this works
 * before any jQuery is on the page - mobile loads its Sprockets bundle only
 * after the webpack pack has run.
 */
export function initPostBookingTracking(): void {
    CLICK_EVENTS.forEach((eventName: string): void => {
        document.addEventListener(eventName, trackClick, { passive: true });
    });

    watchOptionsRequests();
    watchPageLeaving();
}

/**
 * Reports that the bags/seats/upgrades options reached the screen, and how long
 * they took. Runs once the panels exist - mobile loads this pack from the head,
 * before there is a body to watch.
 */
export function initAncillaryOptionsTracking(): void {
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", observeOptionsContainers);
        return;
    }

    observeOptionsContainers();
}

/**
 * Reports a failed options request. rails-ujs fires jQuery events, and those
 * only reach handlers bound through the same jQuery - desktop runs it from
 * webpack, mobile from Sprockets.
 *
 * @param jq the jQuery instance rails-ujs was loaded against
 */
export function initAncillaryAjaxTracking(jq: any): void {
    if (!jq) {
        return;
    }

    jq(document).on("ajax:error", ANCILLARY_SELECTOR, trackOptionsError);
}
